/**
 * Mode Select ("GAME SETUP"): offline mode (vs CPU / local versus) and the match rules (format, drives per player
 * or the Classic halves, specials, wind, stadium lighting) with a broadcast "match card" that explains the format in
 * plain words. Confirm on the last row (or anywhere with Start) goes to Character Select.
 */
import { MatchFormat, SpecialsMode } from '@hf/shared';
import { useActions, useServices } from '../actions';
import {
  draft,
  DRIVE_CHOICES,
  updateDraft,
  type OfflineMode,
  type VenueSetting,
  type WindSetting,
} from '@hf/client-core/ui-model/flow';
import { KICKOFF_CALL } from '@hf/client-core/ui-model/format';
import { useFocusGrid } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { cycle, Shell, StepRow } from './Shell';

const MODES: readonly OfflineMode[] = ['cpu', 'local'];
const MODE_NAME: Record<string, string> = { cpu: 'VS CPU', local: 'LOCAL VERSUS' };
const SPECIALS: readonly SpecialsMode[] = [SpecialsMode.SIGNATURE, SpecialsMode.OFF];
const SPECIALS_NAME: Record<number, string> = {
  [SpecialsMode.SIGNATURE]: 'SIGNATURE',
  [SpecialsMode.OFF]: 'OFF',
};
const WINDS: readonly WindSetting[] = ['calm', 'breezy', 'gusty'];
const VENUES: readonly VenueSetting[] = ['auto', 'day', 'night'];
const VENUE_NAME: Record<VenueSetting, string> = {
  auto: 'SURPRISE ME',
  day: 'DAY GAME',
  night: 'NIGHT GAME',
};
/** Classic needs the sim's kick plays (kickoff / punt / onside tosses); older sims only offer Drives. */
export const classicAvailable = (): boolean => KICKOFF_CALL >= 0;
const FORMATS: readonly MatchFormat[] = classicAvailable()
  ? [MatchFormat.DRIVES, MatchFormat.CLASSIC]
  : [MatchFormat.DRIVES];
const FORMAT_NAME: Record<number, string> = {
  [MatchFormat.DRIVES]: 'DRIVES',
  [MatchFormat.CLASSIC]: 'CLASSIC',
};

interface SetupRow {
  label: string;
  hint: string;
  value: string;
  step(dir: -1 | 1): void;
}

export function ModeSelect() {
  const actions = useActions();
  const { audio } = useServices();
  const d = draft.value;
  const mode: OfflineMode = d.mode === 'local' ? 'local' : 'cpu';
  const format = FORMATS.includes(d.format) ? d.format : MatchFormat.DRIVES;
  const classic = format === MatchFormat.CLASSIC;

  const rows: SetupRow[] = [
    {
      label: 'MODE',
      hint: mode === 'cpu' ? 'You vs the computer' : 'Two players, one screen',
      value: MODE_NAME[mode]!,
      step: (dir) => updateDraft({ mode: cycle(MODES, mode, dir) }),
    },
    ...(FORMATS.length > 1
      ? [
          {
            label: 'FORMAT',
            hint: classic ? 'Two halves on the clock, kickoffs and punts' : 'Equal possessions, no clock',
            value: FORMAT_NAME[format]!,
            step: (dir: -1 | 1) => updateDraft({ format: cycle(FORMATS, format, dir) }),
          },
        ]
      : []),
    classic
      ? {
          label: 'HALVES',
          hint: 'Game clock per half',
          value: '2 × 2:00',
          step: () => {},
        }
      : {
          label: 'DRIVES',
          hint: 'Possessions per player',
          value: `${d.drives} EACH`,
          step: (dir) =>
            updateDraft({
              drives: cycle(DRIVE_CHOICES, d.drives as (typeof DRIVE_CHOICES)[number], dir, false),
            }),
        },
    {
      label: 'SPECIALS',
      hint: 'Signature moves at full power',
      value: SPECIALS_NAME[d.specials] ?? 'OFF',
      step: (dir) => updateDraft({ specials: cycle(SPECIALS, d.specials, dir) }),
    },
    {
      label: 'WIND',
      hint: 'Pushes the ball in flight',
      value: d.wind.toUpperCase(),
      step: (dir) => updateDraft({ wind: cycle(WINDS, d.wind, dir, false) }),
    },
    {
      label: 'STADIUM',
      hint: 'Sunday afternoon or under the lights',
      value: VENUE_NAME[d.venue] ?? VENUE_NAME.auto,
      step: (dir) => updateDraft({ venue: cycle(VENUES, d.venue, dir) }),
    },
  ];
  const go = (): void => {
    playSfx(audio, UI_SFX.confirm);
    if (d.mode !== mode || d.format !== format) updateDraft({ mode, format });
    actions.goto('character_select');
  };
  const grid = useFocusGrid({
    count: rows.length + 1,
    onMove: () => playSfx(audio, UI_SFX.move),
    onAdjust: (i, dir) => {
      const r = rows[i];
      if (!r) return;
      r.step(dir);
      playSfx(audio, UI_SFX.toggle);
    },
    onConfirm: (i) => {
      if (i === rows.length) go();
      else grid.setIndex(i + 1);
    },
    onPause: go,
    onBack: () => {
      playSfx(audio, UI_SFX.back);
      actions.goto('main_menu');
    },
  });

  return (
    <Shell
      screen="mode_select"
      title="GAME SETUP"
      kicker={`${mode === 'cpu' ? 'EXHIBITION · 1 PLAYER' : 'VERSUS · 2 PLAYERS'}${classic ? ' · CLASSIC' : ''}`}
      class="hf-setup"
      onBack={() => actions.goto('main_menu')}
      hints={[
        ['left|right', 'CHANGE'],
        ['confirm', 'NEXT'],
        ['pause', 'PICK HEADS'],
        ['back', 'BACK'],
      ]}
    >
      <div class="hf-setup__grid">
        <div class="hf-setup__rows">
          {rows.map((r, i) => (
            <StepRow
              key={r.label}
              label={r.label}
              hint={r.hint}
              value={r.value}
              focused={grid.index === i}
              onStep={r.step}
              item={grid.item(i)}
            />
          ))}
          <button class="hf-btn hf-setup__go interactive" {...grid.item(rows.length)}>
            PICK YOUR HEADS ▶
          </button>
        </div>
        <aside class="hf-matchcard hf-panel" aria-label="Match card">
          <div class="hf-matchcard__band t-broadcast">
            {d.venue === 'day' ? "TODAY'S RULES" : d.venue === 'night' ? "TONIGHT'S RULES" : 'MATCH RULES'}
          </div>
          <ul>
            {classic ? (
              <>
                <li>
                  <b>Two halves × 2:00</b> on the game clock. Most points when time runs out wins; tied =
                  overtime rounds, both sides get a drive.
                </li>
                <li>
                  Every half and every score starts with a <b>kickoff toss</b>: hold Action for power, release
                  to kick. Hold ▼ at the release for an <b>onside kick</b>.
                </li>
                <li>
                  Stuck on 4th down? Hold <b>Special + Action</b> at the snap to <b>punt</b> it away.
                </li>
              </>
            ) : (
              <li>
                <b>{d.drives} drives each</b>, alternating. Every drive starts at the opponent's <b>40</b>.
              </li>
            )}
            <li>
              <b>4 downs to gain 10 yards</b>. Make it: new set of downs. Miss it: turnover on downs.
            </li>
            <li>
              Touchdown <b>6</b> · extra-point toss <b>1</b> or 2-point play <b>2</b> · field-goal toss{' '}
              <b>3</b> · safety <b>2</b>.
            </li>
            {classic ? null : (
              <li>
                Tied after regulation? <b>Overtime rounds</b> from the 25: both sides get a drive, a lead
                after the round wins.
              </li>
            )}
            <li>
              {d.specials === SpecialsMode.OFF
                ? 'Specials are OFF: pure skill, every head plays its stats.'
                : "Specials ON: fill the power meter with yards, tackles and good fakes to unleash your head's signature move."}
            </li>
            <li>
              {d.wind === 'calm'
                ? // only name the time of day when the stadium choice fixes it (SURPRISE ME can be a day game)
                  `${d.venue === 'day' ? 'Calm afternoon' : d.venue === 'night' ? 'Calm night' : 'Calm air'}: the ball flies true.`
                : `${d.wind === 'gusty' ? 'Gusty' : 'Breezy'}: wind pushes every throw — the same way for both sides.`}
            </li>
          </ul>
        </aside>
      </div>
    </Shell>
  );
}
